import React, { useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { KalenderBegivenhed } from '../../types';
import { formaterDato } from '../../utils/dateUtils';
import Badge from '../shared/Badge';
import DannebrogFlag from '../shared/DannebrogFlag';
import styles from './DayDetailDrawer.module.scss';

interface DayDetailDrawerProps {
  dato: Date | null;
  begivenheder: KalenderBegivenhed[];
  onLuk: () => void;
}

function TypeIcon({ type }: { type: string }) {
  if (type === 'officiel') return <DannebrogFlag size={20} />;
  if (type === 'foedselsdag') return <>🎂</>;
  return <>📅</>;
}

const DayDetailDrawer: React.FC<DayDetailDrawerProps> = ({ dato, begivenheder, onLuk }) => {
  const isOpen = dato !== null;

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Filter events for the selected date
  const dagsBegivenheder = dato
    ? begivenheder.filter(
        (b) =>
          b.dato.getFullYear() === dato.getFullYear() &&
          b.dato.getMonth() === dato.getMonth() &&
          b.dato.getDate() === dato.getDate()
      )
    : [];

  return (
    <>
      {/* Backdrop */}
      <div
        className={`${styles.backdrop} ${isOpen ? styles.open : ''}`}
        onClick={onLuk}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        className={`${styles.drawer} ${isOpen ? styles.open : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={dato ? formaterDato(dato) : ''}
      >
        <div className={styles.handle} aria-hidden="true" />

        <div className={styles.header}>
          <div>
            <h2 className={styles.title}>
              {dato ? dato.getDate() + '.' : ''}
            </h2>
            <p className={styles.subtitle}>
              {dato ? formaterDato(dato) : ''}
            </p>
          </div>
          <button onClick={onLuk} className={styles.closeBtn} aria-label="Luk">
            <XMarkIcon className={styles.closeIcon} />
          </button>
        </div>

        <div className={styles.content}>
          {dagsBegivenheder.length === 0 ? (
            <p className={styles.empty}>Ingen begivenheder denne dag.</p>
          ) : (
            <ul className={styles.list}>
              {dagsBegivenheder.map((evt) => (
                <li key={evt.id} className={`${styles.item} ${styles[`item-${evt.type}`]}`}>
                  <span className={styles.emoji} aria-hidden="true">
                    <TypeIcon type={evt.type} />
                  </span>
                  <div className={styles.itemContent}>
                    <div className={styles.itemHeader}>
                      <span className={styles.itemTitle}>{evt.titel}</span>
                      <Badge type={evt.type} />
                    </div>
                    {evt.beskrivelse && (
                      <p className={styles.itemDesc}>{evt.beskrivelse}</p>
                    )}
                    {evt.alder !== undefined && (
                      <p className={styles.itemMeta}>Fylder {evt.alder} år</p>
                    )}
                    {(evt.vej || evt.vejnummer) && (
                      <p className={styles.itemMeta}>
                        📍 {[evt.vej, evt.vejnummer].filter(Boolean).join(' ')}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
};

export default DayDetailDrawer;
